import { useEffect, useState } from "react";
import type { BrandIcon } from "./brand-icons.generated";

/**
 * Brand logos load once, on demand, so the logo module stays out of the first bundle.
 * `loadBrandLogos` starts the load (main.tsx calls it at start-up); `useBrandLogos` gives a
 * component the module once it has arrived and re-renders it when it does.
 */

export type Logos = { brandIcon: (key: string) => BrandIcon | undefined };

let loaded: Logos | null = null;
let loading: Promise<Logos> | null = null;

export function loadBrandLogos(): Promise<Logos> {
  loading ??= import("./brand-icons.generated").then((m) => {
    loaded = m;
    return m;
  });
  return loading;
}

export function useBrandLogos(): Logos | null {
  const [logos, setLogos] = useState<Logos | null>(loaded);
  useEffect(() => {
    if (logos) return;
    let live = true;
    void loadBrandLogos().then((m) => {
      if (live) setLogos(m);
    });
    return () => {
      live = false;
    };
  }, [logos]);
  return logos;
}

/** Whether a logo exists for this connector key, once the logos have loaded. */
export function useHasBrandLogo(brand: string): boolean {
  return Boolean(useBrandLogos()?.brandIcon(brand));
}
